import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import './ChestPurchaseModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {getChestIconPath} from '../../../utils/inventory/getChestIconPath';
import {getFormattedPrice} from '../../../utils/price/getFormattedPrice';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';
import {CHESTS} from "../../../constants/chests";
import {WIN_POINTS_COLOR_LIGHT} from "../../../styles/constants";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {MINUS_SIGN} from "../../../constants/text";
import {calcChestsTotalPrice} from "../../../utils/price/calcChestsTotalPrice";

const PAGE_ID = 'ChestPurchaseModal';
const MAX_QUANTITY = 99;
const JUST_PURCHASED_DURATION_MS = 10000;

const REWARD_TYPE_LABELS = {
  [INVENTORY_ITEMS.FACTION_SKIN]: 'Образ фракции',
  [INVENTORY_ITEMS.FACTION_UNIT_SKIN]: 'Образ отрядов фракции',
  [INVENTORY_ITEMS.FACTION]: 'Фракция',
  [INVENTORY_ITEMS.ALL_FACTIONS]: 'Все фракции текущие и будущие',
};

function getRewardRange(reward) {
  return reward.count ? `${getFormattedPrice(reward.count.min)} – ${getFormattedPrice(reward.count.max)}` : '';
}

export function ChestRewardLabel({reward}) {
  if (reward.itemType === INVENTORY_ITEMS.NUMENS) {
    return (
      <span className="chest-purchase-reward-label text-coins">
        {getRewardRange(reward)}
        <img className="chest-purchase-reward-icon" src="/img/icons/numens.png" alt="Нумены"/>
      </span>
    );
  }

  if (reward.itemType === INVENTORY_ITEMS.CRYSTALS) {
    return (
      <span className="chest-purchase-reward-label text-crystals">
        {getRewardRange(reward)}
        <img className="chest-purchase-reward-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
      </span>
    );
  }

  // Премиум: count задан в ДНЯХ (см. конфиги сундуков), поэтому не проценты и
  // не валюта — диапазон с явной единицей измерения
  if (reward.itemType === INVENTORY_ITEMS.PREMIUM) {
    return (
      <span className="chest-purchase-reward-label text-coins">
        {reward.count
          ? `Премиум ${reward.count.min} ${MINUS_SIGN} ${reward.count.max} дн.`
          : 'Премиум'}
        <img className="chest-purchase-reward-icon" src="/img/icons/premium.png" alt="Премиум"/>
      </span>
    );
  }

  if (reward.itemType === INVENTORY_ITEMS.FACTION || reward.itemType === INVENTORY_ITEMS.ALL_FACTIONS || reward.itemType === INVENTORY_ITEMS.FACTION_SKIN) {
    return (
      <span className="chest-purchase-reward-label chest-purchase-reward-label_neutral">
        {REWARD_TYPE_LABELS[reward.itemType]}
        <img className="chest-purchase-reward-icon" src="/img/icons/banner.png" alt="Фракция"/>
      </span>
    );
  }

  if (reward.itemType === INVENTORY_ITEMS.FACTION_UNIT_SKIN) {
    return (
      <span className="chest-purchase-reward-label chest-purchase-reward-label_neutral">
        {REWARD_TYPE_LABELS[reward.itemType]}
        <img
          className="chest-purchase-reward-icon chest-purchase-reward-icon_unit-skin"
          src="/img/icons/unit d20 white.png" alt="Образ отрядов фракции"
        />
      </span>
    );
  }

  return (
    <span className="chest-purchase-reward-label chest-purchase-reward-label_neutral">
      {REWARD_TYPE_LABELS[reward.itemType] || reward.itemType}
    </span>
  );
}

/**
 * Таблица «Награда / Вероятность» сундука — в модалке покупки и в модалке
 * содержимого сундука (ChestContentsModal)
 */
export function ChestRewardsList({rewards}) {
  return (
    <ul className="chest-purchase-rewards-list">
      <li key={'header'} className="chest-purchase-reward-row">
        <span className="chest-purchase-reward-label chest-purchase-reward-label_neutral">Награда</span>
        <span className="chest-purchase-reward-chance">Вероятность</span>
      </li>
      {/* Награды — в обратном порядке относительно конфига
          сундука: самое ценное (в конфиге последнее) сверху */}
      {[...rewards].reverse().map((reward, index) => (
        <li key={index} className="chest-purchase-reward-row">
          <ChestRewardLabel reward={reward}/>
          <span className="chest-purchase-reward-chance">{reward.chance}%</span>
        </li>
      ))}
    </ul>
  );
}

function ChestPurchaseModal({chest, onClose, onPurchase, onOpenInventory, alreadyOwned, initialQuantity = 1}) {
  // Сырая дата из стора, активность премиума считается на каждый рендер —
  // не замораживать в useState (модалка может смонтироваться до ответа /me)
  const premiumExpiresAt = useSelector((state) => state.private.auth.premiumExpiresAt);
  const isPremiumActive = Boolean(premiumExpiresAt) && new Date(premiumExpiresAt).getTime() > Date.now();
  const [visible, setVisible] = useState(false);
  const [quantity, setQuantity] = useState(String(initialQuantity));
  const [purchasing, setPurchasing] = useState(false);
  const [justPurchased, setJustPurchased] = useState(Boolean(alreadyOwned));
  const justPurchasedTimerRef = useRef(null);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => () => clearTimeout(justPurchasedTimerRef.current), []);

  const handleClose = () => {
    if (purchasing) return;
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!chest) return null;

  const isEternal = chest.id === CHESTS.ETERNAL;
  const isNumensPrice = !chest.priceCrystals && chest.priceNumens > 0;
  const parsedQuantity = parseInt(quantity, 10);
  const isQuantityValid = Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= MAX_QUANTITY;
  // Оптовая скидка (discountPercentsFor10ItemsPurchase, от 10 шт.) и
  // премиум-скидка на сундуки за кристаллы уже учтены в totalPrice; при
  // ненулевой суммарной скидке на кнопке «Купить» рисуется анимированный
  // бейдж ее размера (проценты складываются: 15% опт + 10% премиум = 25%)
  const {totalPrice, effectiveDiscountPercents} = isQuantityValid
    ? calcChestsTotalPrice(chest, parsedQuantity, {isPremiumActive})
    : {totalPrice: 0, effectiveDiscountPercents: 0};
  const currencyIcon = isNumensPrice ? '/img/icons/numens.png' : '/img/icons/crystal.png';
  const currencyAlt = isNumensPrice ? 'Нумены' : 'Кристаллы';

  const handleQuantityStep = (delta) => {
    if (purchasing) return;
    // Тот же звук сундука, что и при открытии модалки (плашка магазина играет
    // playChestOpen; Eternal здесь не бывает — у него нет блока количества)
    MENU_SOUND_MANAGER.playChestOpen();
    const current = parseInt(quantity, 10);
    const base = Number.isInteger(current) ? current : 1;
    const next = Math.min(MAX_QUANTITY, Math.max(1, base + delta));
    setQuantity(String(next));
  };

  const handleBuyClick = async () => {
    if (!isQuantityValid || purchasing) return;
    setPurchasing(true);
    try {
      await onPurchase?.(chest.id, parsedQuantity);
      MENU_SOUND_MANAGER.playCoins(totalPrice);
      setJustPurchased(true);
      clearTimeout(justPurchasedTimerRef.current);
      if (!isEternal) {
        // Unique items (like the Eternal chest) stay in the "purchased" state
        // permanently — the buy button should never revert for them.
        justPurchasedTimerRef.current = setTimeout(() => {
          setJustPurchased(false);
        }, JUST_PURCHASED_DURATION_MS);
      }
    } catch (e) {
      // The purchase error is already surfaced to the user by the caller;
      // keep the modal open so they can retry.
    } finally {
      setPurchasing(false);
    }
  };

  const handleOpenInventoryClick = () => {
    onOpenInventory?.();
  };

  return (
    <div
      className={`chest-purchase-overlay ${visible ? 'chest-purchase-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      style={{'--chest-shadow-color': chest.shadowColor ?? WIN_POINTS_COLOR_LIGHT}}
    >
      <div className={`chest-purchase-modal ${visible ? 'chest-purchase-modal_visible' : ''}`}>
        <div className="chest-purchase-content">
          <div className="chest-purchase-image-wrapper">
            <img
              className="chest-purchase-image"
              src={getChestIconPath(chest.id)}
              alt={chest.name}
            />
          </div>

          <h2
            className="chest-purchase-name"
            style={{
              color: chest.modalTextColor || chest.textColor
            }}
          >
            {chest.name}
          </h2>

          <div className="chest-purchase-rewards">
            {isEternal && justPurchased ? (
              <div className="chest-purchase-eternal-message">
                <p>Поздравляем!</p>
                <p>
                  Вы уже открыли Сундук Вечности.
                </p>
                <p>
                  Каждая новая фракции будет доступна
                  <br/>
                  Вам сразу после её релиза!
                </p>
              </div>
            ) : (
              <>
                {/*<span className="chest-purchase-rewards-title">Возможные награды</span>*/}
                <ChestRewardsList rewards={chest.rewards}/>
              </>
            )}
          </div>

          {!isEternal && (
            <div className="chest-purchase-quantity">
              <button
                type="button"
                className="chest-purchase-quantity-step"
                onClick={() => handleQuantityStep(-1)}
                disabled={purchasing}
                aria-label="Уменьшить количество"
              >
                {MINUS_SIGN}
              </button>
              <input
                id="chest-purchase-quantity-input"
                className="chest-purchase-quantity-input"
                type="number"
                min="1"
                max={MAX_QUANTITY}
                value={quantity}
                onChange={(event) => {
                  // Больше 2 цифр не ввести (MAX_QUANTITY = 99): maxLength у
                  // type="number" браузеры игнорируют — обрезаем сами
                  setQuantity(event.target.value.slice(0, 2));
                }}
                disabled={purchasing}
              />
              <button
                type="button"
                className="chest-purchase-quantity-step"
                onClick={() => handleQuantityStep(1)}
                disabled={purchasing}
                aria-label="Увеличить количество"
              >
                +
              </button>
              <div className="chest-purchase-quantity-label">
                шт. =
              </div>

              <div className={`chest-purchase-total-value ${isNumensPrice ? 'text-coins-dark' : ''}`}>
                {getFormattedPrice(totalPrice)}
                <img className="chest-purchase-total-icon" src={currencyIcon} alt={currencyAlt}/>
              </div>
            </div>
          )}

          <div className="main-menu-buttons chest-purchase-buttons">
            <PageButton
              className="chest-purchase-buy-button"
              onClick={justPurchased ? handleOpenInventoryClick : handleBuyClick}
              pageId={PAGE_ID}
              buttonId={justPurchased ? 'open_inventory' : 'confirm'}
              disabled={!justPurchased && (!isQuantityValid || purchasing)}
            >
              {purchasing ? 'Покупка...' : (
                justPurchased ? 'Открыть коллекцию' : (
                  isEternal ? (
                    /* У Вечности премиум-скидка тоже отражается бейджем —
                       та же span-обертка вокруг всей подписи с ценой */
                    <span
                      className={`chest-purchase-buy-button-text ${effectiveDiscountPercents > 0 ? 'chest-purchase-buy-button-text_discounted' : ''}`}
                      data-discount={`${MINUS_SIGN}${effectiveDiscountPercents}%`}
                    >
                      Купить за
                      <span className={'price-container text-crystals'}>
                        {getFormattedPrice(totalPrice)}
                        <img className="chest-purchase-buy-button-icon" src={currencyIcon} alt={currencyAlt}/>
                      </span>
                    </span>
                  ) : (
                    /* Бейдж суммарной скидки (опт + премиум) — псевдоэлементом
                       span-обертки текста (::after с content: attr(data-discount)):
                       абсолютное позиционирование от правого края ТЕКСТА не
                       участвует в раскладке — центровка подписи не сбивается */
                    <span
                      className={`chest-purchase-buy-button-text ${effectiveDiscountPercents > 0 ? 'chest-purchase-buy-button-text_discounted' : ''}`}
                      data-discount={`${MINUS_SIGN}${effectiveDiscountPercents}%`}
                    >
                      Купить
                    </span>
                  )
                )
              )}
            </PageButton>

            <PageButton
              className="main-menu-button_secondary"
              onClick={handleClose}
              pageId={PAGE_ID}
              buttonId="hide"
              disabled={purchasing}
            >
              Скрыть
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChestPurchaseModal;
